import {Input, Modal, NumberInput, Select} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {DateInput} from '@mantine/dates';
import {zodResolver} from '@hookform/resolvers/zod';
import React, {useEffect, useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {
  InspectionScheduleRequest,
  InspectionScheduleResponse,
} from 'src/api/ProactApiClient';
import {InspectionScheduleCadenceEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {useGetInspectionSchedulesBySiteQuery} from 'src/redux/queries/inspectionSchedule';
import {useGetInspectionTypeSelectOptionsQuery} from 'src/redux/queries/inspectionType';
import {useGetSiteListV2Query} from 'src/redux/queries/site';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {
  findDuplicateSchedules,
  selectOptionsToData,
} from './inspectionScheduleHelpers';
import type {ScheduleFormValues} from './scheduleFormValidation';
import {scheduleFormSchema} from './scheduleFormValidation';

const SELECT_QUERY = {
  start: 0,
  length: 10000,
  filters: [],
  filterFns: [],
  sorting: [],
};

const CADENCE_OPTIONS = [
  {label: 'Quarterly', value: String(InspectionScheduleCadenceEnum.Quarterly)},
  {label: 'Annual', value: String(InspectionScheduleCadenceEnum.Annual)},
  {label: 'Custom', value: String(InspectionScheduleCadenceEnum.Custom)},
];

export interface InspectionScheduleFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** Fixes the schedule to one site and hides the Site picker. */
  siteId?: number;
  editingSchedule?: InspectionScheduleResponse;
  isSaving: boolean;
  onSave: (request: InspectionScheduleRequest) => Promise<void>;
}

const emptyValues: ScheduleFormValues = {
  siteId: '',
  inspectionTypeId: '',
  cadence: InspectionScheduleCadenceEnum.Quarterly,
  intervalDays: null,
  anchorDate: new Date(),
  leadTimeDays: 7,
};

/**
 * Create/edit modal for an inspection recurrence schedule.
 * @registry Modal form for creating or editing an inspection recurrence schedule.
 */
export const InspectionScheduleFormModal: React.FC<
  InspectionScheduleFormModalProps
> = ({opened, onClose, siteId, editingSchedule, isSaving, onSave}) => {
  const showSiteSelect = siteId === undefined;

  const {data: inspectionTypes} = useGetInspectionTypeSelectOptionsQuery(
    SELECT_QUERY,
    {skip: !opened}
  );
  const {data: sites} = useGetSiteListV2Query(SELECT_QUERY, {
    skip: !opened || !showSiteSelect,
  });

  const typeOptions = useMemo(
    () => selectOptionsToData(inspectionTypes?.data),
    [inspectionTypes]
  );

  // Sites with no time zone cannot resolve a due date, so they are not offered —
  // except the edited schedule's own site, whose read-only value must resolve.
  const siteOptions = useMemo(() => {
    const options = (sites?.data ?? [])
      .filter(site => !!site.timeZone || site.id === editingSchedule?.siteId)
      .map(site => ({
        value: String(site.id),
        label: site.name ?? String(site.id),
      }));

    if (
      editingSchedule &&
      !options.some(o => o.value === String(editingSchedule.siteId))
    ) {
      options.unshift({
        value: String(editingSchedule.siteId),
        label: editingSchedule.siteName,
      });
    }

    return options;
  }, [sites, editingSchedule]);

  const initialValues = useMemo<ScheduleFormValues>(
    () =>
      editingSchedule
        ? {
            siteId: String(editingSchedule.siteId),
            inspectionTypeId: String(editingSchedule.inspectionTypeId),
            cadence: editingSchedule.cadence,
            intervalDays: editingSchedule.intervalDays ?? null,
            anchorDate: parseUtcDate(editingSchedule.anchorDate),
            leadTimeDays: editingSchedule.leadTimeDays,
          }
        : {...emptyValues, siteId: siteId ? String(siteId) : ''},
    [editingSchedule, siteId]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: {isValid},
  } = useForm<ScheduleFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (opened) reset(initialValues);
  }, [opened, initialValues, reset]);

  const cadence = watch('cadence');
  const selectedSiteId = watch('siteId');
  const selectedTypeId = watch('inspectionTypeId');
  const intervalDays = watch('intervalDays');

  const {data: siteSchedules} = useGetInspectionSchedulesBySiteQuery(
    Number(selectedSiteId),
    {skip: !opened || !selectedSiteId}
  );

  const duplicates = useMemo(() => {
    if (!selectedSiteId || !selectedTypeId) return [];
    return findDuplicateSchedules(siteSchedules ?? [], {
      id: editingSchedule?.id,
      siteId: Number(selectedSiteId),
      inspectionTypeId: Number(selectedTypeId),
      cadence,
      intervalDays: intervalDays ?? undefined,
    });
  }, [
    siteSchedules,
    selectedSiteId,
    selectedTypeId,
    cadence,
    intervalDays,
    editingSchedule,
  ]);

  const onSubmit = async (values: ScheduleFormValues) => {
    await onSave({
      id: editingSchedule?.id,
      siteId: Number(values.siteId),
      inspectionTypeId: Number(values.inspectionTypeId),
      cadence: values.cadence as InspectionScheduleCadenceEnum,
      intervalDays:
        values.cadence === InspectionScheduleCadenceEnum.Custom
          ? (values.intervalDays ?? undefined)
          : undefined,
      anchorDate: values.anchorDate,
      leadTimeDays: values.leadTimeDays,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
      size="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="sm">
          {showSiteSelect && (
            <>
              <Controller
                name="siteId"
                control={control}
                render={({field, fieldState}) => (
                  <Select
                    label="Site"
                    placeholder="Select a site"
                    data={siteOptions}
                    value={field.value || null}
                    onChange={val => field.onChange(val ?? '')}
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                    disabled={!!editingSchedule}
                    searchable
                    required
                  />
                )}
              />
              {!editingSchedule && (
                <InfoAlert>
                  Sites without a time zone are not listed — recurrence due dates
                  are computed in the site&apos;s local time zone.
                </InfoAlert>
              )}
            </>
          )}

          <Controller
            name="inspectionTypeId"
            control={control}
            render={({field, fieldState}) => (
              <Select
                label="Inspection type"
                placeholder="Select an inspection type"
                data={typeOptions}
                value={field.value || null}
                onChange={val => field.onChange(val ?? '')}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                searchable
                required
              />
            )}
          />

          <Controller
            name="cadence"
            control={control}
            render={({field}) => (
              <Input.Wrapper label="Cadence" required>
                <SegmentedControl
                  fullWidth
                  data={CADENCE_OPTIONS}
                  value={String(field.value)}
                  onChange={val => field.onChange(Number(val))}
                />
              </Input.Wrapper>
            )}
          />

          {cadence === InspectionScheduleCadenceEnum.Custom && (
            <Controller
              name="intervalDays"
              control={control}
              render={({field, fieldState}) => (
                <NumberInput
                  label="Interval (days)"
                  placeholder="e.g. 45"
                  min={1}
                  max={1000}
                  value={field.value ?? ''}
                  onChange={val =>
                    field.onChange(val === '' ? null : Number(val))
                  }
                  error={fieldState.error?.message}
                  required
                />
              )}
            />
          )}

          <Controller
            name="anchorDate"
            control={control}
            render={({field, fieldState}) => (
              <DateInput
                label="Anchor date"
                placeholder="First occurrence date"
                description="Occurrences are counted from this date in the site's time zone."
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
                valueFormat="MMM D, YYYY"
                required
              />
            )}
          />

          <Controller
            name="leadTimeDays"
            control={control}
            render={({field, fieldState}) => (
              <NumberInput
                label="Lead time (days)"
                placeholder="Days before due to generate the inspection"
                min={0}
                value={field.value}
                onChange={val => field.onChange(val === '' ? 0 : Number(val))}
                error={fieldState.error?.message}
                required
              />
            )}
          />

          {duplicates.length > 0 && (
            <InfoAlert variant="warning" title="Possible duplicate">
              An active schedule with the same inspection type and cadence
              already exists for this site. Saving creates a second one.
            </InfoAlert>
          )}
        </Stack>

        <Group justify="start" mt="lg">
          <Button variant="subtle" color="black" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!isValid}>
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
